/**
 * What every surface that draws a document needs, in one place.
 *
 * The group's document tab had all of this inline. OSO-612 added a SECOND
 * surface — the permanent per-document page a shared link opens — and OSO-613
 * adds a third, the card a shared link renders as inside a post. Three copies
 * of "how big is this file" is how the three come to disagree about a kilobyte.
 */

/**
 * THE PERMANENT ADDRESS OF A DOCUMENT — OSO-612.
 *
 * `document.url` is a SIGNED media URL with an `exp` in it: copy it into a post
 * or an email and it stops working at the deadline. This address carries no
 * access at all — the page behind it asks who is looking and answers the
 * group's content rule, which is the whole difference between "a link to a
 * file" and "a link that IS the key to a file".
 *
 * THE ID IS THE STABLE PART AND THE SLUG IS NOT. The slug is in the path
 * because a link somebody is about to paste should say where it goes, and
 * because the group's own layout keys off it; the page redirects to the
 * canonical spelling if the group has been renamed since. Nothing about access
 * is decided by it.
 */
export function documentPath(doc: {
  id: number;
  group: { slug: string };
}): string {
  return `/grupper/${doc.group.slug}/dokumenter/${doc.id}`;
}

/** "1,4 MB". Empty for a zero-byte file, which has nothing worth saying. */
export function formatFileSize(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * "7. sep. 2026" — the day a file was published.
 *
 * Not `lib/dates.ts`: those are the EVENT formatters, which carry a weekday and
 * a clock because an event is a moment somebody has to turn up at. A document
 * has a date, and the shelf has no room for more.
 */
export function formatDocumentDate(iso: string): string {
  return new Date(iso).toLocaleDateString("da-DK", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
